import { useState, useEffect, useCallback, useRef } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import './Module3World.scss'
import '../../Animations.css'

import MediaService from '../../services/MediaService'
import { getVideo } from '../../s3Func/s3Download'

import Practice from './FacialExpressionPractice'
import Module3Config from './Module3Config'
import { getUserContent, sleep } from '../../utils'
import WorldContainer from '../../components/World/WorldContainer'
import LottieAnimation from '../../components/Lottie/LottieAnimation'

const Module3World = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const lottieRef = useRef()
	const { login } = useSelector((state) => state.loginReducer, shallowEqual)
	const { module3 } = useSelector((state) => state.module3, shallowEqual)
	const [slidesConfig, setSlidesConfig] = useState({})
	const [displayEndingVideo, setDisplayEndingVideo] = useState(false)
	const [displayPracticeVideo, setDisplayPracticeVideo] = useState(false)
	const [showYouAreAwesome, setShowYouAreAwesome] = useState(false)
	const [showRewardSelection, setShowRewardSelection] = useState(false)

	const [showFlyAnimation, setShowFlyAnimation] = useState(false)
	const [lizardTongueAnimation, setLizardTongueAnimation] = useState('')
	const [flyCatchSound, setFlyCatchSound] = useState('')
	const [showTongueAnimation, setShowTongueAnimation] = useState('')
	const [lizardAnimationEnd, setLizardAnimationEnd] = useState('')
	const [dayFinishedSound, setDayFinishedSound] = useState('')

	const WORLD_IMAGES = {
		progressBar0: '/Module3Images/LizTongue_Empty_100819.svg',
		progressBar1: '/Module3Images/LizTongue_1out3_100819.svg',
		progressBar2: '/Module3Images/LizTongue_2out3_100819.svg',
		progressBarCompleted: '/Module3Images/LizTongue_Complete_100819.svg',
		keyReward: '/Module3Images/RewardIcon_Fly@2x.png',

		gift: '/Images/Gift.png',

		animal: '/Module3Images/Lizard+closing+still+image.gif',
		worldBackground: '/Module3Images/U1M3_DayScene_Landscape.png',
		logoutBtn: '/Module1Images/Logout.png',
		journeyImage: '/Module1Images/bluejourneymap.svg',
	}

	const WORLD_REWARDS = {
		Backpack: '/Module3Images/Reward_Backpack.png',
		BootsCowboy: '/Module3Images/Reward_CowboyBoots.png',
		BootsHiking: '/Module3Images/Reward_HikingBoots.png',
		CactusBarell: '/Module3Images/Reward_SmallCactus1.png',
		CactusPrickly: '/Module3Images/Reward_SmallCactus2.png',
		CactusTall: '/Module3Images/Reward_TallCactus1.png',
		CactusTall2: '/Module3Images/Reward_TallCactus2.png',
		Canteen: '/Module3Images/Reward_Cantina.png',
		Fire: '/Module3Images/Reward_Fire.png',
		Hawk: '/Module3Images/Reward_Bird1.png',
		Lantern: '/Module3Images/Reward_Lamp.png',
		MoonCresent: '/Module3Images/Reward_Moon2.png',
		MoonFull: '/Module3Images/Reward_Moon1.png',
		Owl: '/Module3Images/Reward_Bird2.png',
		Tent: '/Module3Images/Reward_Tent1.png',
		TentOld: '/Module3Images/Reward_Tent2.png',
		Hat: '/Module3Images/Reward_Cowboyhat.png',
		Bandanna: '/Module3Images/Reward_Bandana.png',
	}

	const CHARACTER_REWARDS = {
		Hat: '/Module3Images/Reward_Cowboyhat.png',
		Bandanna: '/Module3Images/Reward_Bandana.png',
	}

	// Get AWS URL World Images
	Object.keys(WORLD_IMAGES).forEach((key) => {
		WORLD_IMAGES[key] = MediaService.getImage(WORLD_IMAGES[key], login)
	})

	// Get AWS URL Reward Images
	Object.keys(WORLD_REWARDS).forEach((key) => {
		WORLD_REWARDS[key] = MediaService.getImage(WORLD_REWARDS[key], login)
	})

	// Get AWS URL Reward Images
	Object.keys(CHARACTER_REWARDS).forEach((key) => {
		CHARACTER_REWARDS[key] = MediaService.getImage(
			CHARACTER_REWARDS[key],
			login
		)
	})

	const createFacialExpressionSlideShow = useCallback(async () => {
		const config = {
			navigate: '/facial-expression',
			json: '',
			song: '',
			recording: '',
		}

		const { learn } = Module3Config[`day${module3.currentday}`]

		if (learn) {
			if (module3.currentday <= 4) {
				config.song = await getVideo(learn.song, login)
			} else {
				const userRecording = await getUserContent(
					learn.recording,
					module3.StudentID,
					login.influencerid,
					login
				  );
				const song = await getUserContent(
				learn.song,
				module3.StudentID,
				login.influencerid,
				login
				);
		
				const demoSong = await getUserContent(
				learn.demoSong,
				module3.StudentID,
				login.influencerid,
				login
				);
				fetch(userRecording).then((response) => {
				if (response.status === 200) {
					config.recording = userRecording;
					config.song = song;
				}
				else{
					config.recording = null;
					config.song = demoSong;
				}
		
				})
			}

			config.json = learn.json

			setSlidesConfig(config)
		}
	}, [])

	const handleFinalAnimationEnd = async () => {
		await sleep(500)

		dispatch({
			type: `MODULE3_SET_DAY_UNLOCK${module3.currentday + 1}`,
			payload: true,
		})

		dispatch({
			type: 'MODULE3_SET_dayCompleted',
			payload: module3.currentday,
		})

		if (module3.currentday !== 9) {
			dispatch({
				type: 'MODULE3_SET_stepCompleted',
				payload: 'notstarted',
			})
			await sleep(100);
			navigate('/facial-expression/navigation')
		} else {
			setDisplayEndingVideo(true)
		}
	}

	const getLizardAnimation = async () => {
		const lizardTongAnimationGif = await getVideo(
			'appfiles/expressions/lizard_fly.gif', login
		)
		const lizardEndAnimationGif = await getVideo(
			'appfiles/expressions/lizard_closing.gif', login
		)

		const flyCatchData = await getVideo(
			'appfiles/expressions/expressions_fly_catch.mp3', login
		)
		const dayFinishedData = await getVideo(
			'appfiles/expressions/day_finished.mp3', login
		)

		setLizardTongueAnimation(lizardTongAnimationGif)
		setLizardAnimationEnd(lizardEndAnimationGif)
		setFlyCatchSound(flyCatchData)
		setDayFinishedSound(dayFinishedData)
	}

	useEffect(() => {
		createFacialExpressionSlideShow()
		getLizardAnimation()
	}, [module3.stepComplete, createFacialExpressionSlideShow])

	const rewardSelectionAnimation = async () => {
		setShowFlyAnimation(true)
		lottieRef.current.anim.goToAndPlay(1, true)
		const flyCatch = new Audio(flyCatchSound)
		flyCatch.play()
		await sleep(4000)
		setShowFlyAnimation(false)
		setShowRewardSelection(true)
	}

	const showFinalAnimation = async () => {
		setShowRewardSelection(false)
		const finalAudio = new Audio(dayFinishedSound)
		finalAudio.play()
		await sleep(100)
		setShowYouAreAwesome(true)
		finalAudio.onended = handleFinalAnimationEnd
	}

	return (
		<>
			<WorldContainer
				name={"Expressions"}
				module={module3}
				moduleName="MODULE3"
				moduleConfig={Module3Config}
				slidesConfig={slidesConfig}
				keyIcon={'fly-grab'}
				practice={
					<Practice
						onPracticeClose={setDisplayPracticeVideo}
						background={WORLD_IMAGES.worldBackground}
						stepComplete={'MODULE3_SET_stepCompleted'}
						module={module3}
					/>
				}
				route="/facial-expression"
				worldImages={WORLD_IMAGES}
				worldRewards={WORLD_REWARDS}
				showRewardSelection={showRewardSelection}
				uuid="expressions"
				onPracticeClick={() => setDisplayPracticeVideo(true)}
				displayPracticeVideo={displayPracticeVideo}
				onRewardKeyClick={rewardSelectionAnimation}
				onRewardSelected={showFinalAnimation}
				displayEndingVideo={displayEndingVideo}
			>
				<div className="lizard-character">
					<div
						id="lizard-animation-fly"
						style={{
							height: showFlyAnimation ? 'auto' : 0,
							opacity: showFlyAnimation ? 1 : 0,
						}}
					>
						<LottieAnimation
							ref={lottieRef}
							jsonUrl="appfiles/expressions/Lizard_Animation.json"
							onAnimationComplete={() => {

							}}
							unit1={true}
							login={login}
						/>
					</div>

					<figure
						style={{
							height: !showFlyAnimation ? 'auto' : 0,
							opacity: !showFlyAnimation ? 1 : 0,
						}}
					>
						{module3.rewards.Hat && (
							<img
								alt=""
								src={CHARACTER_REWARDS.Hat}
								className="world-rewards MODULE3 character-rewards Hat"
							/>
						)}

						{module3.rewards.Bandanna && (
							<img
								alt=""
								src={CHARACTER_REWARDS.Bandanna}
								className="world-rewards MODULE3 character-rewards Bandanna"
							/>
						)}
						<span>
							<img src={WORLD_IMAGES.animal} alt="world character" />
						</span>
					</figure>

					<figure
						style={{
							height: showYouAreAwesome ? 'auto' : 0,
							opacity: showYouAreAwesome ? 1 : 0,
						}}
					>
						<img src={lizardAnimationEnd} alt="lizard end animated character" />
					</figure>
				</div>
			</WorldContainer>
		</>
	)
}

export default Module3World
